/**
 * Baby Care waitlist — click tracking + sign-up panel behaviour.
 *
 * TRACKING
 * Any element carrying [data-baby-waitlist] (rendered by snippets/baby-waitlist-button.liquid)
 * publishes one `baby_waitlist_click` custom event through Shopify's analytics pipeline:
 *
 *   Shopify.analytics.publish('baby_waitlist_click', { product, location, button, placement, page_path })
 *
 * The store's Customer Events pixels (Settings > Customer events) receive it via
 * analytics.subscribe('baby_waitlist_click', ...) and can forward it to GA4 / Meta / etc.
 * A DOM CustomEvent is also dispatched on `document` for any on-page listener.
 *
 * PANEL
 * A [data-baby-waitlist] button whose aria-controls names a [data-baby-waitlist-panel]
 * (snippets/baby-waitlist-form.liquid) opens that panel. The panel also opens when the page
 * is loaded with #waitlist, or after the form posts (so the result is visible).
 *
 * Loaded as a module from the snippets, so it executes once per page even when several
 * buttons are rendered. A second guard below covers the theme editor re-rendering sections.
 */
const EVENT_NAME = 'baby_waitlist_click';
const DEDUPE_MS = 800;
const JS_CLASS = 'baby-wl-js';

if (!window.__babyWaitlistTracking) {
  window.__babyWaitlistTracking = true;

  const root = document.documentElement;
  // In the theme editor the panel stays visible so it can always be edited.
  const inEditor = root.classList.contains('shopify-design-mode');
  const handled = new WeakSet();
  const lastFired = new WeakMap();

  /** @param {HTMLElement} trigger */
  const buildPayload = (trigger) => {
    const { waitlistProduct, waitlistLocation, waitlistButton, waitlistPlacement } = trigger.dataset;
    const payload = {
      product: waitlistProduct || '',
      location: waitlistLocation || '',
      button: waitlistButton || trigger.textContent.trim(),
      page_path: window.location.pathname,
    };
    if (waitlistPlacement) payload.placement = waitlistPlacement;
    return payload;
  };

  /** @param {Record<string, string>} payload */
  const publish = (payload) => {
    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(EVENT_NAME, payload);
      }
    } catch (error) {
      // Tracking must never get in the way of the click itself.
      console.warn(`[baby-waitlist] could not publish ${EVENT_NAME}`, error);
    }

    document.dispatchEvent(new CustomEvent(`baby-care:${EVENT_NAME}`, { detail: payload }));
  };

  /**
   * @param {HTMLElement} panel
   * @param {{ focus?: boolean }} [options]
   */
  const openPanel = (panel, { focus = true } = {}) => {
    panel.classList.add('is-open');
    document.querySelectorAll('[data-baby-waitlist][aria-controls="' + panel.id + '"]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'true');
    });
    if (focus) {
      const input = panel.querySelector('input[type="email"]');
      if (input) input.focus({ preventScroll: true });
    }
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-baby-waitlist]');
    if (!trigger || handled.has(event)) return;
    handled.add(event);

    // Open the panel synchronously so the browser's own #anchor scroll lands on a visible element.
    const panelId = trigger.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    if (panel) openPanel(panel);

    // One physical click can be reported twice by some browsers/overlays, and a fast double-tap is
    // still one intent: allow one event per button per DEDUPE_MS.
    const now = Date.now();
    if (now - (lastFired.get(trigger) || 0) < DEDUPE_MS) return;
    lastFired.set(trigger, now);

    publish(buildPayload(trigger));
  });

  const init = () => {
    const panels = Array.from(document.querySelectorAll('[data-baby-waitlist-panel]'));
    if (!panels.length || inEditor) return;

    root.classList.add(JS_CLASS);

    // Arriving from a "Join the Waitlist" button on another page (…/baby-care#waitlist)
    if (window.location.hash === '#waitlist') openPanel(panels[0], { focus: false });

    // After the form posts, Shopify reloads the page: show the result straight away.
    panels.forEach((panel) => {
      if (!panel.querySelector('[data-baby-waitlist-status]')) return;
      openPanel(panel, { focus: false });
      window.setTimeout(() => panel.scrollIntoView({ block: 'center' }), 250);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
}
